import { useCallback, useEffect, useRef, useState } from 'react';

/** 経過分の表示を進める間隔。旧地図配車 (PlanningStatistics.presenter.tsx:250) と同じ */
export const TICK_MS = 20 * 1000;

/** これを**超えたら**自分から取り直す（同 :253 の `ago > 30`） */
export const AUTO_REFRESH_MINUTES = 30;

type Params = {
  /** 配送状況の最終更新日時 (ISO)。まだ取れていなければ null */
  lastUpdatedAt: string | null | undefined;
  onRefresh: () => void;
};

type Result = {
  /**
   * 新しい更新を見つけてからの経過分。0 なら「更新あり」ではない。
   * 呼び出し側は 0 のとき lastUpdatedAt だけを出し、再取得ボタンは出さない。
   */
  agoMinutes: number;
  /** 画面に出す最終更新日時。まだ何も取れていなければ null */
  lastUpdatedAt: string | null;
  refresh: () => void;
};

/**
 * 配送状況の鮮度。旧地図配車 (V3Plans presenters/PlanningStatistics.presenter.tsx:236-254)
 * と同じ挙動にする。
 *
 * 平時は最終更新日時だけを出し、再取得ボタンは出さない。3 分ごとのポーリング
 * (useQueryLastOperationStatusUpdated の refetchInterval) が新しい更新を拾ったときだけ
 * 「N分前に更新あり」を出し、放置されたら 30 分超で自分から取り直す。
 *
 * Why 「知っている値」を別に持つ: ポーリングの戻り値をそのまま出すと、初回表示から
 * ずっと「更新あり」になる。最初に見た値は既に画面へ反映済みなので、そこからの差分だけを更新と見なす。
 */
export function useOperationStatusFreshness({ lastUpdatedAt, onRefresh }: Params): Result {
  // 画面が既に知っている最終更新。ここから変わったものだけを「新しい更新」と見なす
  const [knownAt, setKnownAt] = useState<string | null>(null);
  const [agoMinutes, setAgoMinutes] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 依存に入れると、呼び出し側が毎回作り直したときに tick が止まる
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const clearTick = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 表示したまま放置されるので、時間経過に合わせて数字を進める
  const startTick = useCallback((updatedAt: string) => {
    clearTick();
    const ago = Math.floor((Date.now() - new Date(updatedAt).valueOf()) / 1000 / 60);
    setAgoMinutes(ago);

    if (ago > AUTO_REFRESH_MINUTES) {
      // 見ていない画面が古い状態のまま残らないようにする
      setKnownAt(updatedAt);
      setAgoMinutes(0);
      onRefreshRef.current();
      return;
    }
    timerRef.current = setTimeout(() => startTick(updatedAt), TICK_MS);
  }, [clearTick]);

  useEffect(() => {
    if (!lastUpdatedAt) return;
    // 初回に見た値は「もう知っている更新」。ここでボタンを出すと押す用の無い操作が常駐する
    if (knownAt === null) {
      setKnownAt(lastUpdatedAt);
      return;
    }
    if (knownAt !== lastUpdatedAt) startTick(lastUpdatedAt);
  }, [knownAt, lastUpdatedAt, startTick]);

  // 画面を離れたあとも動き続けると、消えたコンポーネントを更新して警告が出る
  useEffect(() => clearTick, [clearTick]);

  const refresh = useCallback(() => {
    clearTick();
    setKnownAt(lastUpdatedAt ?? null);
    setAgoMinutes(0);
    onRefreshRef.current();
  }, [clearTick, lastUpdatedAt]);

  return { agoMinutes, lastUpdatedAt: knownAt, refresh };
}
